import type { TeacherAllocation } from '@/types/allocation';
import type { HomeworkItem } from '@/types/homework';
import type { ExamOrigin } from '@/types/exam';
import type { AllocatedClass, TaughtClass } from './types/teacher';

/**
 * Turns what the server sends into what the console reads.
 *
 * One place rather than one per screen: the teacher area has seven screens
 * hanging off the same list, and each of them working out for itself that a
 * class teacher is `isClassTeacher === true` is how two of them end up
 * disagreeing (CLAUDE.md §3).
 */
export function toAllocatedClass(allocation: TeacherAllocation): AllocatedClass {
  return {
    id: allocation.uuid,
    classSectionUuid: allocation.classSectionUuid,
    className: allocation.displayName,
    grade: allocation.className,
    section: allocation.sectionName,
    subject: allocation.subject?.name ?? null,
    subjectUuid: allocation.subject?.uuid ?? null,
    role: allocation.isClassTeacher ? 'Class Teacher' : 'Subject Teacher',
    totalStudents: allocation.totalStudents,
  };
}

/** Narrows to the classes that carry a subject — see `TaughtClass`. */
export function isTaughtClass(item: AllocatedClass): item is TaughtClass {
  return item.subject !== null && item.subjectUuid !== null;
}

/**
 * "Class 8 - A — Mathematics (Subject Teacher)", as a class picker names an
 * allotment.
 *
 * The subject part is left out for a class-teacher-only allotment, whose
 * subject is `null` — printing it gave "Class 8 - A — null (Class Teacher)".
 */
export function allotmentLabel(item: AllocatedClass): string {
  return item.subject
    ? `${item.className} — ${item.subject} (${item.role})`
    : `${item.className} (${item.role})`;
}

/**
 * The class filter's options, one per section.
 *
 * A teacher who takes two subjects in 8-A holds two allotments for it, and a
 * filter listing "Class 8 - A" twice reads as a bug.
 */
export function distinctClassNames(allocated: AllocatedClass[]): string[] {
  return [...new Set(allocated.map((item) => item.className))];
}

/**
 * The same sections, with the id beside the label.
 *
 * `distinctClassNames` answers this for a filter that only compares what is on
 * screen; a picker that *posts* has to send the section's uuid, and building
 * that list per screen is how one of them ends up sending a class name to an
 * endpoint expecting an id.
 */
export function distinctSections(allocated: AllocatedClass[]): { uuid: string; name: string }[] {
  const byUuid = new Map<string, string>();
  for (const item of allocated) byUuid.set(item.classSectionUuid, item.className);

  return [...byUuid].map(([uuid, name]) => ({ uuid, name }));
}

/**
 * The subjects this teacher takes, once each.
 *
 * The same shape as `distinctSections` and for the same reason: a teacher who
 * takes Telugu in three sections holds three allotments for it, and a filter
 * listing it three times reads as a bug.
 */
export function distinctSubjects(allocated: AllocatedClass[]): { uuid: string; name: string }[] {
  const byUuid = new Map<string, string>();
  for (const item of allocated) {
    if (isTaughtClass(item)) byUuid.set(item.subjectUuid, item.subject);
  }

  return [...byUuid].map(([uuid, name]) => ({ uuid, name }));
}

/**
 * "07 Aug 2026" — a `YYYY-MM-DD` as an institute writes it.
 *
 * Read in UTC to match how these dates are stored and sent. Parsing one as a
 * local instant is how "2026-08-07" becomes the 6th for anybody west of
 * Greenwich, which on a due date is a day of somebody's homework.
 */
export function formatInstituteDate(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * "Monday, Nov 2, 2026" — a `YYYY-MM-DD` with its weekday, as a page header
 * dates itself.
 *
 * Read in UTC for the same reason as `formatInstituteDate`; pass it
 * `instituteToday()` rather than formatting `new Date()`, whose day is the
 * device's and not the school's.
 */
export function formatInstituteLongDate(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Today at the institute, as `YYYY-MM-DD` — the same shape every date the API
 * sends arrives in, so the two can be compared as strings.
 *
 * Read in IST rather than UTC: before half past five in the morning the two are
 * on different dates, and a paper due "tomorrow" must not read as sat already.
 * `en-CA` is the one common locale that formats a date as `YYYY-MM-DD`.
 */
export function instituteToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

/**
 * Minutes since midnight at the institute, for comparing against a period's
 * `HH:mm` times.
 *
 * Read in IST for the same reason as `instituteToday`: the bell schedule is the
 * school's clock, and a phone set to another zone would otherwise call a period
 * finished hours early or late.
 */
export function instituteMinutesNow(): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const read = (type: 'hour' | 'minute') =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);

  return read('hour') * 60 + read('minute');
}

/**
 * The institute's wall clock as `YYYY-MM-DD HH:MM`, which sorts as a string.
 *
 * Built from `instituteToday` and `instituteMinutesNow` so the day and the time
 * are both asked of India — comparing a paper's `examDate` + `endTime` against
 * it needs no `Date` arithmetic for a zone to move.
 */
export function instituteNow(): string {
  const minutes = instituteMinutesNow();
  const clock = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

  return `${instituteToday()} ${clock}`;
}

/** "05:45 pm, 07 Aug" — an ISO instant as the institute reads it. */
export function formatInstituteDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * How many days a due date is from today, as a short phrase.
 *
 * Compared as date strings rather than as instants: both sides are calendar
 * days, and turning them into times only invites a zone to move one of them.
 */
export function dueLabel(dueDate: string, today: string): { text: string; isOverdue: boolean } {
  if (dueDate === today) return { text: 'Due today', isOverdue: false };
  if (dueDate < today) return { text: `Due ${formatInstituteDate(dueDate)}`, isOverdue: true };

  return { text: `Due ${formatInstituteDate(dueDate)}`, isOverdue: false };
}

/**
 * Whether everybody on the roster has handed one piece of work in.
 *
 * The roster is checked for anybody at all first: a section with nobody in it
 * satisfies `0 >= 0`, and without the guard every empty class would read as
 * finished.
 */
export function isFullyHandedIn(
  homework: Pick<HomeworkItem, 'submissionCount' | 'totalStudents'>,
): boolean {
  return homework.totalStudents > 0 && homework.submissionCount >= homework.totalStudents;
}

/** Where one piece of work stands. See `homeworkState`. */
export type HomeworkState = 'draft' | 'completed' | 'open';

/**
 * Not yet published, everybody in, or still open.
 *
 * A draft comes first and outranks the rest: the class has not been given it,
 * so nobody can be finished with it. The other two come from how many have
 * handed in.
 *
 * The due date plays no part. Students may still hand in after it, so work
 * past its date is open until the last child is in — the teacher panel has no
 * "overdue" of its own.
 */
export function homeworkState(homework: HomeworkItem): HomeworkState {
  if (homework.publication === 'DRAFT') return 'draft';
  if (isFullyHandedIn(homework)) return 'completed';

  return 'open';
}

/**
 * Whether the work may still be corrected: a draft always, published work up to
 * and including its due day.
 *
 * Mirrors the server, which refuses anything later with `HOMEWORK_PAST_DUE` —
 * this only keeps the option off the menu so nobody is offered a door that is
 * locked. Independent of `homeworkState`: work everybody has handed in early is
 * still before its date.
 */
export function isHomeworkEditable(homework: HomeworkItem, today: string): boolean {
  return homework.publication === 'DRAFT' || homework.dueDate >= today;
}

/**
 * Which exams tab is open, kept in the address of the exams page.
 *
 * In the address and not in state so that leaving for a sheet or the class
 * results and coming back — by the breadcrumb or the browser's Back — lands on
 * the tab the teacher left. Class tests, the default, is left off the address.
 */
const EXAM_ORIGIN_PARAM = 'origin';

export function examOriginFrom(params: URLSearchParams): ExamOrigin {
  return params.get(EXAM_ORIGIN_PARAM) === 'INSTITUTE' ? 'INSTITUTE' : 'CLASS_TEST';
}

/** `path` with the tab on it, so the screen it leads to can lead back to the same one. */
export function withExamOrigin(path: string, origin: ExamOrigin): string {
  return origin === 'INSTITUTE' ? `${path}?${EXAM_ORIGIN_PARAM}=${origin}` : path;
}

/** Sets the tab in `params`, leaving every other part of the address as it was. */
export function setExamOrigin(params: URLSearchParams, origin: ExamOrigin): URLSearchParams {
  const next = new URLSearchParams(params);
  if (origin === 'INSTITUTE') next.set(EXAM_ORIGIN_PARAM, origin);
  else next.delete(EXAM_ORIGIN_PARAM);
  return next;
}
